import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, CheckCircle, Snowflake, WashingMachine, Utensils, Flame } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { BrandsSlider } from '../components/BrandsSlider';
import { Testimonials } from '../components/Testimonials';

// JSON-LD Schema for Richmond Hill Page
const richmondHillSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Infinity Appliances Repair - Richmond Hill",
  "image": "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136",
  "telephone": "+1-647-919-6893",
  "priceRange": "$$",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Richmond Hill",
    "addressRegion": "ON",
    "addressCountry": "CA"
  },
  "areaServed": [
    { "@type": "City", "name": "Richmond Hill" },
    { "@type": "City", "name": "Oak Ridges" }
  ],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "reviewCount": "150"
  }
};

const services = [
  { icon: Snowflake, title: 'Refrigerator Repair', description: 'Expert fridge diagnostics and repair' },
  { icon: WashingMachine, title: 'Washer & Dryer', description: 'Same-day laundry appliance service' },
  { icon: Utensils, title: 'Dishwasher Repair', description: 'Fix leaks and drainage issues' },
  { icon: Flame, title: 'Stove & Oven', description: 'Get cooking again fast' },
];

export const RichmondHillPage = () => {
  useEffect(() => {
    // Set SEO meta tags
    document.title = 'Richmond Hill Appliance Repair | 2-Hour Arrival | Infinity';
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Reliable appliance repair in Richmond Hill & Oak Ridges. Same-day service for all major brands. Licensed, insured, and local. Book your 2-hour window: 647-919-6893.';

    // JSON-LD Schema
    let schemaScript = document.querySelector('#richmond-hill-schema');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'richmond-hill-schema';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(richmondHillSchema);

    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="richmond-hill-page">
      {/* Hero Section */}
      <section 
        className="hero-overlay relative min-h-[70vh] flex items-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1660643063795-769aee073bcb?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="hero-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            {/* Breadcrumb */}
            <nav className="mb-6 text-white/70 text-sm">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-white">Richmond Hill</span>
            </nav>

            {/* Trust Badge */}
            <div className="trust-badge mb-8">
              <MapPin size={18} className="text-[#FF6B00]" />
              <span className="text-white font-semibold">Serving Richmond Hill & Oak Ridges</span>
            </div>

            {/* H1 */}
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Professional Appliance Repair Services in Richmond Hill
            </h1>

            {/* Description */}
            <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-2xl">
              Serving Oak Ridges and the Bayview corridor. Our Richmond Hill technicians are specialized in Samsung, LG, and Bosch repairs.
            </p>

            {/* CTA */}
            <a
              href="tel:647-919-6893"
              className="pulse-cta inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg text-white transition-transform hover:scale-105 shadow-xl"
              style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }}
              data-testid="richmond-hill-hero-cta"
            >
              <Phone size={22} />
              <span>Call 647-919-6893</span>
            </a>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left Content */}
            <div>
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}
              >
                Reliable Appliance Repair Richmond Hill
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-6">
                Living in Richmond Hill means you shouldn't have to wait days for a repair. Whether you're near Bayview Secondary or the Elgin Mills area, our technicians are minutes away.
              </p>
              <p className="text-[#475569] leading-relaxed mb-8">
                We specialize in high-end brand repairs (Samsung, LG, Bosch) for Richmond Hill residents, ensuring your stove or dishwasher is back in action before dinner time. Our quick, professional service covers all Richmond Hill neighborhoods 24/7.
              </p>

              {/* Features */}
              <div className="space-y-4 mb-8">
                {[
                  '2-Hour Arrival Windows',
                  'Samsung, LG & Bosch Specialists',
                  'Same-Day Service Available',
                  'Oak Ridges Coverage',
                  'No Diagnostic Fees with Repair',
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-3">
                    <CheckCircle size={20} style={{ color: '#FF6B00' }} />
                    <span className="font-medium" style={{ color: '#1A2B4C' }}>{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <a
                href="tel:647-919-6893"
                className="inline-flex items-center gap-3 px-6 py-3 rounded-full font-bold text-white transition-transform hover:scale-105"
                style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }}
                data-testid="richmond-hill-content-cta"
              >
                <Phone size={18} />
                <span>Schedule Your Richmond Hill Repair</span>
              </a>
            </div>

            {/* Right - Map */}
            <div>
              <div className="map-container rounded-2xl overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d92004.49652599!2d-79.48763!3d43.8828!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b2a4605f8cf67%3A0x5051289c88a54e9f!2sRichmond%20Hill%2C%20ON!5e0!3m2!1sen!2sca!4v1234567890"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Richmond Hill Service Area Map"
                  data-testid="richmond-hill-map"
                ></iframe>
              </div>
              <p className="text-center text-[#475569] mt-4 text-sm">
                Serving all Richmond Hill neighborhoods including Oak Ridges, Bayview corridor, and Elgin Mills
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-[#F8F9FC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl font-bold text-center mb-10"
            style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}
          >
            Our Richmond Hill Repair Services
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <Card 
                  key={service.title}
                  className="glass-card border-0 shadow-lg hover:shadow-xl transition-shadow"
                  data-testid={`richmond-hill-service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <CardContent className="p-6 text-center">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                      style={{ background: 'rgba(255, 107, 0, 0.1)' }}
                    >
                      <IconComponent size={28} style={{ color: '#FF6B00' }} />
                    </div>
                    <h3 
                      className="font-bold mb-2"
                      style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}
                    >
                      {service.title}
                    </h3>
                    <p className="text-sm text-[#475569]">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <BrandsSlider />
      <Testimonials />

      {/* Final CTA */}
      <section className="py-16" style={{ background: '#1A2B4C' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Need Appliance Repair in Richmond Hill?
          </h2>
          <p className="text-white/70 mb-8 text-lg">
            Our Richmond Hill technicians are ready to help. Same-day service available.
          </p>
          <a
            href="tel:647-919-6893"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg text-white transition-transform hover:scale-105"
            style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }}
            data-testid="richmond-hill-final-cta"
          >
            <Phone size={22} />
            <span>Call 647-919-6893 Now</span>
          </a>
        </div>
      </section>
    </main>
  );
};

export default RichmondHillPage;
